import React, { useState, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  LineController,
  PointElement,
  Filler,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Chart } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  LineController,
  PointElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

/**
 * CumulativeWealthChart Component
 * 
 * Displays cumulative (running total) of projected income vs spending over time
 * Green line: Cumulative projected income
 * Red line with fill: Cumulative spending
 * 
 * @param {Object} props Component props
 * @param {Array} props.incomeTrends Array of income trend data
 * @param {Array} props.spendingTrends Array of spending trend data
 * @param {boolean} props.isLoading Loading state indicator
 * @param {number} props.projectedMonthlyIncome Monthly projected income from Income sources
 * @returns {JSX.Element} CumulativeWealthChart component
 */
const CumulativeWealthChart = ({ 
  incomeTrends = [], 
  spendingTrends = [], 
  isLoading = false,
  projectedMonthlyIncome = 0
}) => {
  const [dataMode, setDataMode] = useState('projected'); // 'projected' or 'actual'

  // Combine all months from both datasets
  const allMonths = useMemo(() => {
    const monthSet = new Set();
    incomeTrends.forEach(item => monthSet.add(item.month));
    spendingTrends.forEach(item => monthSet.add(item.month));
    return Array.from(monthSet).sort();
  }, [incomeTrends, spendingTrends]);

  // Calculate cumulative values
  const { cumulativeIncome, cumulativeSpending } = useMemo(() => {
    let runningIncome = 0;
    let runningSpending = 0;
    const cumIncome = [];
    const cumSpending = [];

    allMonths.forEach(month => {
      if (dataMode === 'projected') {
        // Use projected monthly income
        runningIncome += projectedMonthlyIncome;
      } else {
        // Use actual income from transactions
        const incomeData = incomeTrends.find(item => item.month === month);
        runningIncome += incomeData ? incomeData.amount : 0;
      }

      // Spending is always actual from transactions
      const spendingData = spendingTrends.find(item => item.month === month);
      runningSpending += spendingData ? spendingData.amount : 0;

      cumIncome.push(runningIncome);
      cumSpending.push(runningSpending);
    });

    return {
      cumulativeIncome: cumIncome,
      cumulativeSpending: cumSpending
    };
  }, [allMonths, incomeTrends, spendingTrends, dataMode, projectedMonthlyIncome]);

  // Format months for display (MMM YY)
  const formattedMonths = allMonths.map(month => {
    const [year, monthNum] = month.split('-');
    const date = new Date(year, parseInt(monthNum) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  });

  const data = {
    labels: formattedMonths,
    datasets: [
      {
        type: 'line',
        label: dataMode === 'projected' ? 'Cumulative Projected Income' : 'Cumulative Actual Income',
        data: cumulativeIncome,
        borderColor: 'rgba(34, 197, 94, 1)', // Green
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        borderWidth: 3,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: 'rgba(34, 197, 94, 1)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        fill: false,
        order: 1
      },
      {
        type: 'line',
        label: 'Cumulative Spending',
        data: cumulativeSpending,
        borderColor: 'rgba(239, 68, 68, 1)', // Red
        backgroundColor: 'rgba(239, 68, 68, 0.2)',
        borderWidth: 3,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: 'rgba(239, 68, 68, 1)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        fill: true, // Fill under the line
        order: 2
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: '500'
          }
        }
      },
      datalabels: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 13,
          weight: 'bold'
        },
        bodyFont: {
          size: 12
        },
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            label += new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(context.parsed.y);
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 11
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            size: 11
          },
          callback: function(value) {
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(value);
          }
        }
      }
    }
  };

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="h-4 w-32 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-2">
            <div className="h-2 w-48 bg-gray-200 rounded"></div>
            <div className="h-2 w-40 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (allMonths.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="text-gray-400 text-sm">No data available</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      {/* Toggle for Projected vs Actual */}
      <div className="flex justify-end mb-2">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 sm:p-1">
          <button
            onClick={() => setDataMode('projected')}
            className={`px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-sm font-medium rounded-md transition-colors ${
              dataMode === 'projected'
                ? 'bg-violet-600 text-white'
                : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            <span className="sm:hidden">P</span>
            <span className="hidden sm:inline">Projected</span>
          </button>
          <button
            onClick={() => setDataMode('actual')}
            className={`px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-sm font-medium rounded-md transition-colors ${
              dataMode === 'actual'
                ? 'bg-violet-600 text-white'
                : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            <span className="sm:hidden">A</span>
            <span className="hidden sm:inline">Actual</span>
          </button>
        </div>
      </div>
      
      {/* Chart */}
      <div className="flex-1">
        <Chart type="line" data={data} options={options} key={dataMode + JSON.stringify(allMonths)} />
      </div>
    </div>
  );
};

export default CumulativeWealthChart;

